import React, { useEffect, useRef } from "react";

export interface ContextMenuItem {
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  divider?: boolean;
}

export interface ContextMenuProps {
  isOpen: boolean;
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
  className?: string;
}

export default function ContextMenu({ isOpen, x, y, items, onClose, className = "" }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`fixed z-50 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[160px] ${className}`}
      style={{
        left: x,
        top: y,
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {item.divider ? (
            <div className="my-1 border-b border-[#F1F5F9]" />
          ) : (
            <div className="px-2">
              <button
                className={`w-full px-4 py-[6px] text-left rounded-md text-base font-medium hover:bg-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed flex items-center cursor-pointer ${
                  item.disabled ? "text-gray-400" : "text-gray-700"
                }`}
                onClick={() => {
                  if (!item.disabled && item.onClick) {
                    item.onClick();
                    onClose();
                  }
                }}
                disabled={item.disabled}
              >
                {item.icon && <span className="w-4 h-4 mr-2">{item.icon}</span>}
                {item.label}
              </button>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
